/**
 * React Query hooks for the conversation-memory session read APIs and the
 * playground-context bootstrap, used by the editor's history rail + resume.
 *
 *   - `GET /api/playground/context`        → shared playground Contact id
 *   - `GET /api/sessions?contactId=…`      → history rail (newest-first)
 *   - `GET /api/sessions/:id/messages`     → a session's transcript
 */

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { apiRequest } from "./client";
import type {
  PlaygroundContextResponse,
  SessionListResponse,
  SessionMessageListResponse,
} from "./types";

export const sessionKeys = {
  playgroundContext: ["playground", "context"] as const,
  list: (contactId: string, agentId?: string) =>
    ["sessions", "list", contactId, agentId ?? null] as const,
  messages: (sessionId: string) => ["sessions", "messages", sessionId] as const,
};

export function usePlaygroundContext() {
  return useQuery({
    queryKey: sessionKeys.playgroundContext,
    queryFn: () => apiRequest<PlaygroundContextResponse>("GET", "/api/playground/context"),
    staleTime: Infinity,
  });
}

/**
 * The shared playground contact's sessions. `agentId` (passed only by the
 * workflow-agent playground) scopes the list to that agent's own test sessions;
 * omitted → the full shared list (unchanged for every chat-agent playground).
 */
export function useSessionList(contactId: string | undefined, agentId?: string) {
  return useQuery({
    queryKey: sessionKeys.list(contactId ?? "", agentId),
    queryFn: () =>
      apiRequest<SessionListResponse>("GET", "/api/sessions", undefined, {
        contactId,
        ...(agentId ? { agentId } : {}),
      }),
    enabled: Boolean(contactId),
  });
}

/**
 * A session's transcript, oldest-first, cursor-paginated. The detail view
 * appends newer pages downward via infinite scroll (no "load more" button),
 * so a long conversation isn't truncated to the first page.
 */
export function useSessionMessages(sessionId: string | undefined, limit?: number) {
  return useInfiniteQuery({
    queryKey: [...sessionKeys.messages(sessionId ?? ""), limit] as const,
    queryFn: ({ pageParam }) =>
      apiRequest<SessionMessageListResponse>(
        "GET",
        `/api/sessions/${sessionId}/messages`,
        undefined,
        { cursor: pageParam, limit },
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: Boolean(sessionId),
  });
}

/** Record a card's submitted client-state (Phase 5 lock-after-submit). */
export function recordCardSubmit(
  sessionId: string,
  messageId: string,
  state: Record<string, unknown>,
): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>(
    "POST",
    `/api/sessions/${sessionId}/messages/${messageId}/card-submit`,
    { state },
  );
}
